import React from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Divider
} from "@mui/material";
import _ from "lodash";

import { Tag, ModalCard } from "@dishopsaas/design-system";

import MapWithLatLng from "../../components/Map/MapWithLatLng";
import ModalBody from "../../components/ModalBody/ModalBody.view";

import { getAddress } from "../../components/InfoBar/InfoBar.services";
import { sortOpenHours } from "../../components/Cover/Horaires/Horaires.services";

import { ORDER_TYPE_DELIVERY, ORDER_TYPES } from "../../constants";
import { getColors, hasAnyDeliverySlots } from "../../utils";

type Day =
  | "LUNDI"
  | "MARDI"
  | "MERCREDI"
  | "JEUDI"
  | "VENDREDI"
  | "SAMEDI"
  | "DIMANCHE";

interface DayHours {
  name: Day;
  slots: string[];
  slotsDelivery?: string[];
}

interface RestaurantHoursModalProps {
  open: boolean;
  onClose: () => void;
  address: any;
  addressShop: any;
  statusLabel: string;
  openHours: DayHours[];
  googleApiKey: string;
  secondaryColor: string;
  theme: string;
  orderTypes: string[];
  shopName: string;
}

const RestaurantHoursModal: React.FC<RestaurantHoursModalProps> = ({
  open,
  onClose,
  address,
  addressShop,
  statusLabel,
  openHours,
  googleApiKey,
  secondaryColor,
  theme: mapTheme,
  orderTypes,
  shopName,
}) => {
  const [tab, setTab] = React.useState(0);
  const { text, border, surfaceSecondary } = getColors();

  const currentOrderType = orderTypes[tab];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const renderOpenHours = () => {
    if (!openHours) return null;

    const sorted = sortOpenHours(openHours);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          bgcolor: surfaceSecondary,
          gap: 1,
          borderRadius: 1,
          p: 4,
        }}
      >
        {_.map(sorted, (day, index) => {
          const { name, slots, slotsDelivery } = day;
          const deliveryMode = currentOrderType === ORDER_TYPE_DELIVERY && hasAnyDeliverySlots(sorted);
          const selectedSlots: string[] =
            deliveryMode
              ? slotsDelivery
              : slots;

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {name}
              </Typography>

              <Box sx={{ flex: 1, mx: 2 }}>
                <Divider />
              </Box>

              <Box sx={{ textAlign: "right" }}>
                {!selectedSlots || selectedSlots.length === 0 ? (
                  <Typography variant="subtitle2" >FERMÉ</Typography>
                ) : (
                  selectedSlots.map((slot, i) => {
                    if (!slot) return null;

                    const [start, end] = slot.split("-");

                    return (
                      <Typography variant="subtitle2" key={i}>
                        {start} → {end}
                      </Typography>
                    );
                  })
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };
  const selectedAddress = currentOrderType === ORDER_TYPE_DELIVERY ? address ?? addressShop : addressShop;
  const latitude = selectedAddress?.location?.latitude;
  const longitude = selectedAddress?.location?.longitude;
  return (
    <ModalCard open={open} maxWidth={450} customStyle={{ p: 0 }} onClose={onClose}>
      <ModalBody title="HORAIRES DU RESTAURANT" onClose={onClose} sxBody={{ p: 0 }}>
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            slotProps={{
              indicator: { style: { display: "none" } },
            }}
            sx={{
              borderTop: border,
              borderBottom: border,
              p: 2,
              width: "100%",
              minHeight: 32,
              "& .MuiTabs-flexContainer": {
                justifyContent: "flex-start",
                gap: 2,
              },
            }}
          >
            {orderTypes.map((type) => (
              <Tab
                key={type}
                label={ORDER_TYPES[type]}
                sx={{
                  fontSize: 14,
                  borderRadius: 2,
                  color: text,
                  minHeight: 32,
                  height: 32,
                  padding: 0,
                  "&.Mui-selected": {
                    bgcolor: surfaceSecondary,
                    color: text,
                  },
                }}
              />
            ))}
          </Tabs>
          <Box sx={{ p: 4 }}>
            <MapWithLatLng
              isDelivery={currentOrderType === ORDER_TYPE_DELIVERY}
              lat={latitude}
              lng={longitude}
              apiKey={googleApiKey}
              secondaryColor={secondaryColor}
              theme={mapTheme}
              isInteractive={false}
            />
            <Box sx={{ my: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 1 }}>
                <Typography variant="subtitle1">{shopName}</Typography>
                <Tag label={statusLabel} shape="linear" borderRadius={16} />
              </Box>

              <Typography variant="body1" color={text} mb={2}>
                {getAddress(currentOrderType)}
              </Typography>
            </Box>


            {renderOpenHours()}
          </Box>
        </Box>
      </ModalBody>
    </ModalCard>
  );
};

export default RestaurantHoursModal;
